import logError from 'App/babel/helpers/logError';
import AppService from 'App/legacy/AppService';
import { NOTIFICATION_TYPES } from 'App/babel/enum/notification';
import { currencyFormat } from 'Components/Filters/Currencies';
import T from 'Components/Helpers/translate';

const isActiveTab = () => {
	return !window.document.hidden;
};

const doShow = (notification, options, title, body) => {
	var tag = title + body;

	var browserNotification = new window.Notification(title, {
		body: body,
		tag: tag,
		icon: options.hasIcon ? 'img/browser_notification_icon.png' : '',
		requireInteraction: options.autoHide ? false : true
	});

	browserNotification.onclick = function () {
		options.onClick(notification);
		window.focus();
		this.close();
	};
};

/**********************************/

export const hasPermission = () => {
	return (window.Notification && window.Notification.permission === 'granted') || false;
};

export const requestPermission = () => {
	// Make sure feature is available in browser
	if (!window.Notification) {
		return;
	}
	if (!hasPermission()) {
		window.Notification.requestPermission();
	}
};

export const parseAppMessage = notification => {
	if (notification.message?.includes('"easybooking"')) {
		try {
			const message = JSON.parse(notification.message);
			const { contact, appointment } = message.easybooking;
			return (
				`${contact.name} ${T('default.booked').toLowerCase()} ${appointment.description}` +
				` ${T('notifications.sharedView.withYou')} @ ${moment(appointment.date).format('YYYY-MM-DD - HH:mm')}`
			);
		} catch (e) {
			logError(e, 'Could not parse json');
		}
	}
	return notification.message ? T(notification.message) : '';
};

const showStandardNotification = (notification, options) => {
	const Lang = {
		closedDeal: T('default.closedDeal').toLowerCase(),
		defaultTitle: T('notifications.browser.defaultTitle'),
		defaultBody: T('notifications.browser.defaultBody'),
		sharedView: T('notifications.sharedView').toLowerCase(),
		theView: T('filters.theView'),
		wasShared: T('notifications.wasShared').toLowerCase(),
		sharedViewWithYou: T('notifications.sharedView.withYou').toLowerCase(),
		createdAgreement: T('default.createdAgreement').toLowerCase(),
		purchasedFrom: T('default.purchasedFrom').toLowerCase(),
		yearlyValue: T('default.yearlyValue').toLowerCase(),
		important: T('default.important'),
		message: T('default.message').toLowerCase(),
		youBeenAssigned: T('system.you_been_assigned'),
		didAssign: T('default.didAssign').toLowerCase(),
		toYou: T('default.toYou').toLowerCase(),
		at: T('default.at').toLowerCase(),
		visitedTitle: T('event.visited'),
		visited: T('event.visited').toLowerCase(),
		formSubmitedTitle: T('event.form.submited'),
		formSubmited: T('event.form.submited').toLowerCase(),
		exportTitle: T('export.export'),
		export: T('export.export').toLowerCase(),
		completed: T('default.done').toLowerCase(),
		failed: T('noti.failed').toLowerCase(),
		hadMarketingEvent: T('event.hadMarketingEvent'),
		marketingEvent: T('default.marketEvent'),
		esign: T('default.eSign'),
		didSign: T('esign.didSign').toLowerCase(),
		viewed: T('esign.viewed').toLowerCase(),
		rejected: T('esign.rejected').toLowerCase(),
		undelivered: T('esign.undelivered').toLowerCase(),
		contributionMargin: T('default.contributionMarginShort'),
		monthlyValue: T('default.monthlyValue'),
		annualValue: T('default.annualValue'),
		oneOffValue: T('default.oneOffValue'),
		worth: T('notifications.order.value.worth').toLowerCase(),
		with: T('default.with').toLowerCase()
	};

	const NotificationType = NOTIFICATION_TYPES;
	const defaultCurrencyIso = _.find(AppService.getMetadata().customerCurrencies, { masterCurrency: true }).iso;
	const fullName = notification.registeredBy ? notification.registeredBy.name : '';
	const nameSplit = fullName.split(' ');
	const firstName = nameSplit[0] + (nameSplit.length > 1 ? ' ' + nameSplit[1][0] : '');
	const clientName = notification.client && notification.client.name;
	const contactName = notification.contact && notification.contact.name;
	const metadata = AppService.getMetadata();

	let title = Lang.defaultTitle;
	let body = Lang.defaultBody;

	switch (notification.type) {
		case NotificationType.ORDER: {
			const salesModel = metadata.params.SalesModel;
			const salesModelOption = metadata.params.SalesModelOption;
			const showContributionMargin = salesModel === 'cm' && salesModelOption === 'cm';
			const showRecurring = salesModel === 'rr';
			const showARR = showRecurring && salesModelOption === 'arr';

			let recurringValue = 0;
			let orderValue = 0;
			let oneOffValue = 0;
			let displayRecurring, displayARR, displayMRR, displayOneOff, displayCM;

			if (notification.order) {
				let formatted;
				if (showContributionMargin) {
					formatted = currencyFormat(
						notification.order.contributionMarginLocalCurrency,
						notification.order.currency || defaultCurrencyIso
					);
					displayCM = `${formatted} (${Lang.contributionMargin})`;
				} else if (showRecurring) {
					if (showARR) {
						recurringValue = notification.order.annualValue;
						formatted = currencyFormat(
							notification.order.annualValue,
							notification.order.currency || defaultCurrencyIso
						);
						displayARR = recurringValue ? `${formatted} (${Lang.annualValue})` : null;
					} else {
						recurringValue = notification.order.monthlyValue;
						formatted = currencyFormat(
							notification.order.monthlyValue,
							notification.order.currency || defaultCurrencyIso
						);
						displayMRR = recurringValue ? `${formatted} (${Lang.monthlyValue})` : null;
					}
					oneOffValue = notification.order.oneOffValue;
					formatted = currencyFormat(
						notification.order.oneOffValue,
						notification.order.currency || defaultCurrencyIso
					);
					displayOneOff = oneOffValue ? `${formatted} (${Lang.oneOffValue})` : null;

					displayRecurring = displayARR || displayMRR;
					if (displayOneOff) {
						if (displayRecurring) {
							displayRecurring = displayRecurring + ' & ' + displayOneOff;
						} else {
							displayRecurring = displayOneOff;
						}
					}
				}
				orderValue = currencyFormat(
					notification.order ? notification.order.orderValue : 0,
					(notification.order && notification.order.currency) || defaultCurrencyIso
				);
			}
			title = `${firstName} ${Lang.closedDeal}!`;

			body =
				`${notification.registeredBy ? notification.registeredBy.name : ''} ${Lang.closedDeal} ${
					Lang.with
				} ${clientName}` +
				`${' ' + Lang.worth} ${showRecurring ? displayRecurring : ''}` +
				`${showContributionMargin ? displayCM : ''}` +
				`${!showRecurring && !showContributionMargin ? orderValue : ''}.`;
			break;
		}
		case 'ReportView':
		case 'ListView':
			title = `${firstName} ${Lang.sharedView}`;
			body = `${Lang.theView} '${notification.message}' ${Lang.wasShared} ${Lang.sharedViewWithYou}.`;
			break;

		case NotificationType.AGREEMENT: {
			title = `${firstName} ${Lang.createdAgreement}!`;
			const value = currencyFormat(
				notification.agreement ? notification.agreement.yearlyValue : 0,
				(notification.agreement && notification.agreement.currency) || defaultCurrencyIso
			);
			body = `${value} (${Lang.yearlyValue})! ${clientName} ${Lang.purchasedFrom} ${fullName}.`;
			break;
		}
		case NotificationType.SYSTEM:
			title = `${Lang.important} ${Lang.message}`;
			body = notification.message;
			break;
		case NotificationType.APP_CALLBACK:
			title = notification.action;
			body = parseAppMessage(notification);
			break;
		case NotificationType.ASSIGNED:
			title = Lang.youBeenAssigned;
			body = `${fullName} ${Lang.didAssign} ${clientName} ${Lang.toYou}.`;
			break;
		case NotificationType.VISIT:
			title = Lang.visitedTitle;
			body = `${clientName} ${Lang.visited}.`;
			if (contactName) {
				body = `${contactName} ${Lang.at} ` + body;
			}
			break;
		case NotificationType.SUBMIT:
			title = Lang.formSubmitedTitle;
			body = `${contactName} ${Lang.at} ${clientName} ${Lang.formSubmited} ${notification.form.name}.`;
			break;
		case NotificationType.EXPORT:
			if (notification.status !== 100 && notification.status !== -1) {
				// Returning because we only want browser notification for completed/failed exports.
				return;
			}
			if (notification.status === 100) {
				title = `${Lang.exportTitle} ${Lang.completed}`;
				body = notification.name
					? `${notification.name} ${Lang.export} ${Lang.done}`
					: T('noti.export.' + notification.action + '-done');
			}
			if (notification.status === -1) {
				title = `${Lang.exportTitle} ${Lang.failed}`;
				body = notification.name
					? `${notification.name} ${Lang.export} ${Lang.failed}`
					: T('noti.export.' + notification.action + '-failed');
			}
			break;
		case NotificationType.IMPORT:
			if (notification.status !== 100 && notification.status !== -1) {
				// Returning because we only want browser notification for completed/failed imports.
				return;
			}

			title = body = notification.status === 100 ? T('noti.import-done') : T('noti.import-failed');
			break;
		case NotificationType.MARKETING_CUSTOM:
			title = Lang.marketingEvent;
			body = `${clientName} ${Lang.hadMarketingEvent} ${notification.message || ''}.`;
			if (contactName) {
				body = `${contactName} ${Lang.at} ` + body;
			}
			break;
		case NotificationType.ESIGN: {
			var status = '';
			switch (notification.action) {
				case 'signed':
					status = Lang.didSign;
					break;
				case 'seen':
					status = Lang.viewed;
					break;
				case 'declined':
					status = Lang.rejected;
					break;
				case 'undelivered':
					status = Lang.undelivered;
					break;
			}
			var fileName = '';
			if (notification.esign) {
				if (notification.esign.file) {
					fileName = notification.esign.file.filename;
				} else if (notification.esign.title) {
					fileName = notification.esign.title;
				}
			}
			title = Lang.esign;
			body = `${contactName || firstName} ${status} ${fileName}.`;
			break;
		}
		case NotificationType.JOB:
			if (notification.status !== 100 && notification.status !== -1) {
				// Returning because we only want browser notification for completed/failed jobs.
				return;
			}
			var udoName = notification.udo ? notification.udo.name : '';
			if (notification.name) {
				title = body = `${T(notification.action)} ${notification.name} ${
					notification.status === 100 ? Lang.completed : Lang.failed
				}`;
			} else {
				if (notification.status === 100) {
					var split = (notification.message || '').toString().split(':');
					if (
						!['merge-client', 'merge-contact', 'merge-activity', 'merge-appointment'].includes(
							notification.action
						) &&
						split &&
						split.length === 2
					) {
						title = T('noti.' + notification.action + '-partial-done', {
							changed: split[0],
							total: split[1],
							name: udoName
						});
						body = title + ' ' + T('default.missingRights');
					} else if (notification.message === 0) {
						title = T('noti.' + notification.action + '-noop-done', {
							total: notification.message,
							name: udoName
						});
						body = title + ' ' + T('default.missingRights');
					} else {
						title = body = T('noti.' + notification.action + '-done', {
							total: notification.message,
							name: udoName
						});
					}
				} else {
					title = body = T('noti.' + notification.action + '-failed', { name: udoName });
				}
			}
			break;
		default:
			return;
	}

	doShow(notification, options, title, body);
};

export const show = (notification, options) => {
	// Make sure feature is available in browser
	if (!window.Notification) {
		return;
	}
	if (!hasPermission()) {
		requestPermission();
	} else if (options.forceShow || !isActiveTab()) {
		if (notification.type) {
			return showStandardNotification(notification, options);
		}

		doShow(notification, options, notification.title, notification.body);
	}
};
